import network from '@/services/network';
import {
  VpAbrechnungsEbsResponse,
  VpShowquotenResponse, VpEbHerkunftResponse, VpEbDrilldownResponse } from '@/types/vpDashboard';
import {
  ActivityItem,
  DashboardKpis,
  IplannerRevenue,
  LeadershipData,
  MeetingActivityWeek,
  MeetingTodayItem,
  PipelineSummary,
  TodoItem,
  TopPerformer,
} from '@/types';
import { MeineSplitmarge } from '@/types/splitmarge';
import { ErrorResponse } from '@/utils';

const getDashboardKpis = async (): Promise<{ data: DashboardKpis }> => {
  try {
    const { data } = await network.get<{ data: DashboardKpis }>('/dashboard/kpis');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getMeetingActivity = async (): Promise<{ data: MeetingActivityWeek[] }> => {
  try {
    const { data } = await network.get<{ data: MeetingActivityWeek[] }>(
      '/dashboard/meeting-activity',
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getPipelineSummary = async (): Promise<{ data: PipelineSummary }> => {
  try {
    const { data } = await network.get<{ data: PipelineSummary }>('/dashboard/pipeline-summary');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getTodos = async (status: 'open' | 'completed' = 'open'): Promise<{ data: TodoItem[] }> => {
  try {
    const { data } = await network.get<{ data: TodoItem[] }>('/dashboard/todos', { params: { status } });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getRecentActivity = async (): Promise<{ data: ActivityItem[] }> => {
  try {
    const { data } = await network.get<{ data: ActivityItem[] }>('/dashboard/recent-activity');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getTopPerformers = async (): Promise<{ data: TopPerformer[] }> => {
  try {
    const { data } = await network.get<{ data: TopPerformer[] }>('/dashboard/top-performers');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getMeetingsToday = async (): Promise<{ data: MeetingTodayItem[] }> => {
  try {
    const { data } = await network.get<{ data: MeetingTodayItem[] }>('/dashboard/meetings-today');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getIplannerRevenue = async (): Promise<{ data: IplannerRevenue }> => {
  try {
    const { data } = await network.get<{ data: IplannerRevenue }>('/dashboard/iplanner-revenue');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getLeadershipData = async (): Promise<{ data: LeadershipData }> => {
  try {
    const { data } = await network.get<{ data: LeadershipData }>('/dashboard/leadership');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Eigene Kennzahlen des angemeldeten Beraters (Auftrag Alex 23.08.2026).
 *
 * 🔥 NUR `from`/`to`, KEIN `scope` UND KEIN `user_id` — anders als bei
 * `api.cockpit.*`. Die Endpunkte lesen den Berater aus dem Token; ein
 * mitgeschickter Wert waere wirkungslos, und ihn hier anzubieten wuerde
 * genau den Eindruck erwecken, er sei es nicht.
 */
const getEigeneShowquoten = async (
  params: { from: string; to: string },
): Promise<{ data: VpShowquotenResponse }> => {
  try {
    const { data } = await network.get<{ data: VpShowquotenResponse }>(
      '/dashboard/eigene-showquoten',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getAbrechnungsrelevanteEbs = async (
  params: { from: string; to: string },
): Promise<{ data: VpAbrechnungsEbsResponse }> => {
  try {
    const { data } = await network.get<{ data: VpAbrechnungsEbsResponse }>(
      '/dashboard/abrechnungsrelevante-ebs',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getEbNachHerkunft = async (
  params: { from: string; to: string },
): Promise<{ data: VpEbHerkunftResponse }> => {
  try {
    const { data } = await network.get<{ data: VpEbHerkunftResponse }>(
      '/dashboard/eb-herkunft',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getEbDrilldown = async (
  bucket: string,
  params: { from: string; to: string },
): Promise<{ data: VpEbDrilldownResponse }> => {
  try {
    const { data } = await network.get<{ data: VpEbDrilldownResponse }>(
      `/dashboard/eb-herkunft/${bucket}`,
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Eigene Splitmarge des laufenden Monats (Auftrag Alex 09.10.2026).
 *
 * 🔥 KEIN PARAMETER. Der Endpunkt liest den Berater aus dem Token und den
 * Monat aus der Uhr; `data: null` heisst „keine Zeile geliefert".
 */
const getMeineSplitmarge = async (): Promise<{ data: MeineSplitmarge | null }> => {
  try {
    const { data } = await network.get<{ data: MeineSplitmarge | null }>('/dashboard/meine-splitmarge');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const dashboardApi = {
  getDashboardKpis,
  getMeetingActivity,
  getPipelineSummary,
  getTodos,
  getRecentActivity,
  getTopPerformers,
  getMeetingsToday,
  getIplannerRevenue,
  getLeadershipData,
  getEigeneShowquoten,
  getAbrechnungsrelevanteEbs,
  getEbNachHerkunft,
  getEbDrilldown,
  getMeineSplitmarge,
};

export default dashboardApi;
